import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  Add,
  AddCircleOutline,
  CalculateOutlined,
  CancelOutlined,
  CheckCircleOutline,
  CurrencyRupee,
  FileUploadOutlined,
  HistoryOutlined,
  KeyboardArrowDown,
  ListAltOutlined,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { defaultAllSelection } from "services/constants";
import { Loader } from "components/Loader";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import {
  GlobalBreadcrumb,
  sharedStickyFooterBarSx,
  sharedTableBodyCellSx,
  sharedTableHeadCellSx,
} from "components/DesignSystem/sharedUiPatterns";
import {
  APP_BAR_OFFSET,
  StatCard,
  designColors,
  designShell,
  designType,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
  withDesignFont,
} from "services/constants/designTokens";
import { requestBonusSettings, fetchBonusTypes } from "pages/PayRoll/Shared/bonusApi";
import { OpenSettingsButton } from "pages/PayRoll/Shared/settingsSwitch";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { panelBodySx } from "../Shared/formUi";
import { EmployeeCell } from "../Shared/tableUi";
import {
  assignBonusCycle,
  cancelBonusEntry,
  cancelBonusRun,
  listBonusEntries,
  listBonusRuns,
  openBonusWorkspace,
  saveBonusEntry,
} from "./bonusSampleApi";
import {
  Chip,
  DetailRows,
  ENTRY_STATUS,
  SectionCard,
  EntryStatusChips,
  RUN_STATUS,
  SOURCE,
  SourceChip,
  entryHeaders,
  forPeriod,
  money,
  paymentLabel,
  runHeaders,
} from "./bonusUi";
import { BonusEntryDetailsPanel, BonusEntryFormPanel } from "./BonusEntryPanels";
import { BonusRunViewPanel } from "./BonusRunPanels";
import { CalculateBonus } from "./CalculateBonus";
import { BonusImport } from "./BonusImport";

// Payroll > Pay Inputs > Bonus (Cauver Bonus spec v1.6, A5/A6, B5, B8, B9).
// Bonus types and settings come from the real Payroll Settings > Bonus API;
// runs, entries, employees and payroll cycles are SAMPLE data
// (bonusSampleApi.js) until the backend's pay-inputs/bonus/ API is built.

const CARDS = [
  { key: "total", title: "Total", icon: ListAltOutlined, accent: "#2878FF", tint: "#EAF2FF" },
  { key: "ready", title: "Ready", icon: CheckCircleOutline, accent: "#12B76A", tint: "#E7F8EF" },
  { key: "processed", title: "Processed", icon: CurrencyRupee, accent: "#2878FF", tint: "#EAF2FF" },
  { key: "cancelled", title: "Cancelled", icon: CancelOutlined, accent: "#DC2626", tint: "#FEF2F2" },
];

const headSx = { ...sharedTableHeadCellSx, position: "static", whiteSpace: "nowrap" };
const cellSx = { ...sharedTableBodyCellSx, ...designType.tableBodyCell };
const NUMERIC = new Set(["amount", "employees"]);

const cardValue = (rows) => `₹${money(rows.reduce((sum, row) => sum + row.amount, 0))} · ${rows.length.toLocaleString("en-IN")}`;

export const PayInputBonus = () => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  return <BonusScreen key={`${domain}:${topbarCompanyId}`} />;
};

const BonusScreen = () => {
  const { domain, topbarCompanyId, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const validCompany = Number(topbarCompanyId) > 0 && Number(topbarCompanyId) !== defaultAllSelection;
  // No Bonus permission entry exists in provisioning yet (spec B11 lists
  // View / Calculate / Edit / Cancel / Import / Settings); open to all who
  // reach the screen until the backend adds it.
  const canEdit = true;

  const [base, setBase] = useState({ loading: true, error: "", settings: null, types: [], employees: [], cycles: [] });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    if (!validCompany || !domain) { setBase((prev) => ({ ...prev, loading: false })); return () => controller.abort(); }
    setBase((prev) => ({ ...prev, loading: true, error: "" }));
    Promise.all([
      requestBonusSettings({ domain, companyId: topbarCompanyId, signal: controller.signal }),
      fetchBonusTypes({ domain, companyId: topbarCompanyId, signal: controller.signal }),
    ])
      .then(async ([{ config }, types]) => {
        const workspace = await openBonusWorkspace({ companyId: topbarCompanyId, types, rounding: config.rounding });
        if (!controller.signal.aborted) setBase({ loading: false, error: "", settings: config, types, ...workspace });
      })
      .catch((error) => { if (!controller.signal.aborted) setBase((prev) => ({ ...prev, loading: false, error: error.message })); });
    return () => controller.abort();
  }, [domain, topbarCompanyId, validCompany, retry]);

  const { settings, types, employees, cycles } = base;
  const [entries, setEntries] = useState([]);
  const [runs, setRuns] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    if (base.loading || base.error || !settings) return undefined;
    let alive = true;
    setListLoading(true);
    Promise.all([listBonusEntries({ companyId: topbarCompanyId, types }), listBonusRuns({ companyId: topbarCompanyId, types })])
      .then(([nextEntries, nextRuns]) => { if (alive) { setEntries(nextEntries); setRuns(nextRuns); } })
      .catch((error) => showToast("error", error.message))
      .finally(() => { if (alive) setListLoading(false); });
    return () => { alive = false; };
  }, [base.loading, base.error, settings, types, topbarCompanyId, refresh]);
  const reload = () => setRefresh((n) => n + 1);

  const [view, setView] = useState("list");
  const [tab, setTab] = useState("entries");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [entryId, setEntryId] = useState(null);
  const [form, setForm] = useState(null); // { entry } (entry null = add)
  const [formErrors, setFormErrors] = useState({});
  const [runId, setRunId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const sources = settings?.sources || [];
  const runAllowed = sources.includes("BONUS_RUN");
  const manualAllowed = sources.includes("MANUAL");
  const importAllowed = sources.includes("IMPORT");
  const treatment = settings?.treatment || "CURRENT_CYCLE";

  // ---- list rows (flattened for CustomFilter) ----
  const entryRows = useMemo(() => entries.map((entry) => ({
    ...entry,
    date_label: moment(entry.created_at).format("DD MMM YYYY"),
    employee: `${entry.employee_name} (${entry.employee_code})`,
    for_period: forPeriod(entry.period_from, entry.period_to),
    source_label: SOURCE[entry.source],
    status_label: entry.deferred ? "Ready · Deferred" : ENTRY_STATUS[entry.status].label,
    payroll_cycle_name: entry.payroll_cycle?.name || (entry.status === "cancelled" ? "—" : "Not assigned"),
  })), [entries]);
  const runRows = useMemo(() => runs.map((run) => ({
    ...run,
    for_period: forPeriod(run.from_month, run.to_month),
    payment_label: paymentLabel(run.pay_mode, run.part_percent, run.totals.already_paid > 0),
    status_label: RUN_STATUS[run.status].label,
    payroll_cycle_name: run.payroll_cycle?.name || "—",
  })), [runs]);

  const listRows = tab === "entries" ? entryRows : runRows;
  const [filtered, setFiltered] = useState(null);
  const filteredSource = useRef(null);
  const visibleRows = filtered !== null && filteredSource.current === listRows ? filtered : listRows;
  const onFilteredChange = useCallback((data) => {
    filteredSource.current = listRows;
    setFiltered(data);
    setPage(0);
  }, [listRows]);
  const pageRows = visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const cards = {
    total: cardValue(entries),
    ready: cardValue(entries.filter((e) => e.status === "ready" || e.status === "included")),
    processed: cardValue(entries.filter((e) => e.status === "processed")),
    cancelled: cardValue(entries.filter((e) => e.status === "cancelled")),
  };

  const act = async (fn, success) => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(true);
    try {
      await fn();
      if (success) showToast("success", success);
      reload();
      return true;
    } catch (error) {
      showToast("error", error.message);
      return false;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const cancelEntry = async (entry, reason) => {
    if (await act(() => cancelBonusEntry({ companyId: topbarCompanyId, entryId: entry.id, reason }), "Bonus entry cancelled.")) setEntryId(null);
  };
  const assignCycle = (entry, cycleId) => act(
    () => assignBonusCycle({ companyId: topbarCompanyId, entryId: entry.id, cycleId }),
    "Payroll cycle assigned. The entry stays Ready.",
  );
  const cancelRun = async (run, reason) => {
    if (await act(() => cancelBonusRun({ companyId: topbarCompanyId, runId: run.id, reason }), `Run ${run.id} cancelled. Its Ready entries are cancelled.`)) setRunId(null);
  };
  const saveEntry = async (values) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setFormErrors({});
    try {
      await saveBonusEntry({ companyId: topbarCompanyId, entryId: form?.entry?.id, values, types, treatment });
      showToast("success", form?.entry ? "Bonus entry saved." : "Bonus added as Ready.");
      setForm(null);
      reload();
    } catch (error) {
      setFormErrors(error.meta?.fields || {});
      showToast("error", error.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const importBatches = useMemo(() => {
    const map = new Map();
    entries.filter((entry) => entry.source === "import").forEach((entry) => {
      const file = (entry.import_ref || "Import").split(" · ")[0];
      const key = `${file}|${entry.created_at.slice(0, 16)}`;
      const batch = map.get(key) || { file, at: entry.created_at, by: entry.created_by, rows: 0, amount: 0, ready: 0, cancelled: 0 };
      batch.rows += 1;
      batch.amount += entry.amount;
      if (entry.status === "ready") batch.ready += 1;
      if (entry.status === "cancelled") batch.cancelled += 1;
      map.set(key, batch);
    });
    return [...map.values()].sort((a, b) => (a.at < b.at ? 1 : -1));
  }, [entries]);

  if (!validCompany) {
    return (
      <Mui.Box sx={{ ...designShell.pageSurface, p: 2 }}>
        <Mui.Alert severity="info">Select a single company to view bonus.</Mui.Alert>
      </Mui.Box>
    );
  }
  if (base.loading) return <Loader info="Loading..." minHeight="60vh" />;
  if (base.error) {
    return (
      <Mui.Box sx={{ ...designShell.pageSurface, p: 2 }}>
        <Mui.Alert severity="error" action={<Mui.Button color="inherit" onClick={() => setRetry((n) => n + 1)}>Retry</Mui.Button>}>{base.error}</Mui.Alert>
      </Mui.Box>
    );
  }

  if (view === "calculate") {
    return (
      <ThemeProvider theme={withDesignFont}>
        <CalculateBonus
          companyId={topbarCompanyId}
          types={types}
          rounding={settings.rounding}
          employees={employees}
          cycles={cycles}
          onClose={() => setView("list")}
          onAdded={(outcome) => {
            showToast("success", `${outcome.entries_created} bonus entries added to ${outcome.cycle_name} payroll as Ready (run ${outcome.run_id}).`);
            setView("list");
            setTab("entries");
            reload();
          }}
          onOpenRun={(id) => { setView("list"); setTab("runs"); setRunId(id); }}
        />
      </ThemeProvider>
    );
  }

  const entry = entryRows.find((row) => row.id === entryId) || null;
  const run = runRows.find((row) => row.id === runId) || null;
  const columns = tab === "entries" ? entryHeaders : runHeaders;

  const renderEntryCell = (row, key) => {
    switch (key) {
      case "employee": return <EmployeeCell name={row.employee_name} code={row.employee_code} />;
      case "amount": return money(row.amount);
      case "source_label": return <SourceChip entry={row} />;
      case "status_label": return <EntryStatusChips entry={row} />;
      case "payroll_cycle_name": return row.payroll_cycle?.name || "—";
      default: return row[key] ?? "—";
    }
  };
  const renderRunCell = (row, key) => {
    switch (key) {
      case "id": return <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>{row.id}</Mui.Typography>;
      case "amount": return money(row.amount);
      case "status_label": return <Chip {...RUN_STATUS[row.status]} />;
      default: return row[key] ?? "—";
    }
  };

  // "Add Bonus ▾" menu (same pattern as Add Penalty). Add / Import /
  // Calculate follow the Allowed Sources in Bonus settings.
  const addMenuItems = [
    ...(manualAllowed && canEdit ? [{ label: "Add Bonus", icon: AddCircleOutline, onClick: () => { setFormErrors({}); setForm({ entry: null }); } }] : []),
    ...(importAllowed && canEdit ? [{ label: "Import Bonus", icon: FileUploadOutlined, onClick: () => setImportOpen(true) }] : []),
    ...(runAllowed && canEdit ? [{ label: "Calculate Bonus", icon: CalculateOutlined, onClick: () => setView("calculate") }] : []),
    { label: "Import History", icon: HistoryOutlined, onClick: () => setHistoryOpen(true) },
  ];

  return (
    <ThemeProvider theme={withDesignFont}>
      <Mui.Box sx={{ ...designShell.pageSurface, pb: 2 }}>
        <Mui.Box sx={{ ...designShell.headerRowBase, alignItems: { xs: "flex-start", sm: "center" }, px: 2, pt: 2, mb: 2 }}>
          <Mui.Box sx={{ minWidth: 0 }}>
            <GlobalBreadcrumb trail={["Payroll", "Pay Inputs", "Bonus"]} />
            <Mui.Typography component="h1" sx={designType.pageTitle}>Bonus</Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>Statutory, festival and performance bonus. Paid with salary.</Mui.Typography>
          </Mui.Box>
          <Mui.Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1 }}>
            <Mui.Button
              startIcon={<Add />}
              endIcon={<KeyboardArrowDown />}
              disabled={busy || listLoading}
              onClick={(event) => setMoreAnchor(event.currentTarget)}
              aria-haspopup="menu"
              aria-expanded={Boolean(moreAnchor)}
              sx={designShell.primaryButtonBase}
            >
              Add Bonus
            </Mui.Button>
            {/* ⚙ -> Payroll Settings › Bonus */}
            <OpenSettingsButton group="pay-input" item="bonus" />
          </Mui.Stack>
        </Mui.Box>

        <Mui.Menu
          anchorEl={moreAnchor}
          open={Boolean(moreAnchor)}
          onClose={() => setMoreAnchor(null)}
          elevation={0}
          PaperProps={{ sx: { ...designShell.actionMenuPaper, mt: 0.5 } }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
        >
          {addMenuItems.map((item) => (
            <Mui.MenuItem key={item.label} sx={designShell.actionMenuItem} onClick={() => { setMoreAnchor(null); item.onClick(); }}>
              <Mui.ListItemIcon><item.icon fontSize="small" sx={{ color: designColors.tealDark }} /></Mui.ListItemIcon>
              {item.label}
            </Mui.MenuItem>
          ))}
        </Mui.Menu>

        <Mui.Box sx={{ px: 2 }}>
          <Mui.Alert severity="info" sx={{ mb: 1.5 }}>
            Sample data: bonus runs and entries are not saved to the server yet (the backend's Pay Inputs › Bonus API is not built). Bonus types and settings are real.
          </Mui.Alert>

          <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1.5, mb: 2 }}>
            {CARDS.map((card) => (
              <StatCard key={card.key} icon={card.icon} title={card.title} value={cards[card.key]} accent={card.accent} tint={card.tint} />
            ))}
          </Mui.Box>

          <Mui.Box sx={{ ...designShell.sectionCard, px: 2, mb: 1.5 }}>
            <Mui.Tabs value={tab} onChange={(event, next) => { setTab(next); setPage(0); setFiltered(null); }} sx={designShell.panelTabs}>
              <Mui.Tab value="entries" label={`Entries (${entries.length})`} />
              <Mui.Tab value="runs" label={`Bonus runs (${runs.length})`} />
            </Mui.Tabs>
          </Mui.Box>

          <Mui.Box sx={{ position: "sticky", top: APP_BAR_OFFSET, zIndex: 4, backgroundColor: designColors.white }}>
            <CustomFilter
              key={tab}
              title={tab === "entries" ? "Bonus Entries" : "Bonus Runs"}
              filterHeaders={columns}
              data={listRows}
              filteredData={visibleRows}
              onFilteredDataChange={onFilteredChange}
              screenAccess={canEdit}
              hideCount={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
              labeledToolbar={true}
              searchPlaceholder={tab === "entries" ? "Search employee..." : "Search run..."}
            />
          </Mui.Box>

          <Mui.Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            <Mui.TableContainer sx={{ overflowX: "auto" }}>
              <Mui.Table size="small" sx={{ minWidth: 1000 }}>
                <Mui.TableHead>
                  <Mui.TableRow>
                    {columns.map((head) => (
                      <Mui.TableCell key={head.key} align={NUMERIC.has(head.key) ? "right" : "left"} sx={headSx}>{head.name}</Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {listLoading ? (
                    <Mui.TableRow><Mui.TableCell colSpan={columns.length} sx={{ borderBottom: "none" }}><Loader info="Loading..." minHeight="30vh" /></Mui.TableCell></Mui.TableRow>
                  ) : visibleRows.length === 0 ? (
                    <TableEmptyState dataLength={listRows} colSpan={columns.length} />
                  ) : pageRows.map((row) => (
                    <Mui.TableRow
                      key={row.id}
                      hover
                      onClick={() => (tab === "entries" ? setEntryId(row.id) : setRunId(row.id))}
                      sx={{ cursor: "pointer", ...designShell.tableRowHover, ...((tab === "entries" ? entryId : runId) === row.id ? designShell.neutralSelectedRow : {}) }}
                    >
                      {columns.map((head) => (
                        <Mui.TableCell key={head.key} align={NUMERIC.has(head.key) ? "right" : "left"} sx={cellSx}>
                          {tab === "entries" ? renderEntryCell(row, head.key) : renderRunCell(row, head.key)}
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                  ))}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Mui.Typography sx={{ ...designType.captionText, px: 2, py: 1.25 }}>
              {tab === "entries"
                ? "One row per employee per bonus. Entries from a bonus run can be cancelled one by one, or the whole run from the Bonus runs tab."
                : "Click a run to see its employees and month-wise breakdown. A run can be cancelled until its payroll is run — all its entries are cancelled together. Processed runs are locked."}
            </Mui.Typography>
            <Mui.Box sx={sharedStickyFooterBarSx}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: visibleRows.length,
                itemLabel: tab === "entries" ? "entries" : "runs",
                onPageChange: setPage,
                onRowsPerPageChange: (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); },
                rowsPerPageOptions,
              })}
            </Mui.Box>
          </Mui.Box>
        </Mui.Box>
      </Mui.Box>

      {entry && !form ? (
        <BonusEntryDetailsPanel
          entry={entry}
          types={types}
          cycles={cycles}
          busy={busy}
          canEdit={canEdit}
          onClose={() => setEntryId(null)}
          onCancelEntry={cancelEntry}
          onAssignCycle={assignCycle}
          onEdit={(row) => { setFormErrors({}); setForm({ entry: row }); }}
        />
      ) : null}
      {form ? (
        <BonusEntryFormPanel
          key={form.entry?.id || "new"}
          entry={form.entry}
          employees={employees}
          types={types}
          cycles={cycles}
          treatment={treatment}
          saving={busy}
          serverErrors={formErrors}
          onClose={() => setForm(null)}
          onSave={saveEntry}
        />
      ) : null}
      {run ? <BonusRunViewPanel key={run.id} run={run} busy={busy} canEdit={canEdit} onClose={() => setRunId(null)} onCancelRun={cancelRun} /> : null}
      {importOpen ? (
        <BonusImport
          companyId={topbarCompanyId}
          types={types}
          treatment={treatment}
          onClose={() => setImportOpen(false)}
          onImported={({ saved, invalid, duplicate }) => {
            setImportOpen(false);
            showToast("success", `${saved} bonus entr${saved === 1 ? "y" : "ies"} imported as Ready.${invalid ? ` ${invalid} invalid` : ""}${duplicate ? ` · ${duplicate} duplicate not saved` : ""}`);
            setTab("entries");
            reload();
          }}
        />
      ) : null}

      {/* Import History: side panel, like the other Bonus drawers. */}
      {historyOpen ? (
        <GlobalSidePanel
          open
          onClose={() => setHistoryOpen(false)}
          title="Import History"
          subtitle="Bonus files imported for this company, newest first."
          bodySx={panelBodySx}
          footer={
            <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
              <SecondaryButton onClick={() => setHistoryOpen(false)}>Close</SecondaryButton>
            </Mui.Stack>
          }
        >
          {importBatches.length ? (
            <Mui.Stack spacing={2}>
              {importBatches.map((batch) => (
                <SectionCard key={`${batch.file}${batch.at}`} title={batch.file}>
                  <DetailRows
                    rows={[
                      { label: "Imported on", value: moment(batch.at).format("DD MMM YYYY, HH:mm") },
                      { label: "Imported by", value: batch.by },
                      { label: "Rows saved", value: batch.rows },
                      { label: "Amount", value: `₹${money(batch.amount)}` },
                      { label: "Still Ready", value: batch.ready },
                      batch.cancelled ? { label: "Cancelled since", value: batch.cancelled } : null,
                    ]}
                  />
                </SectionCard>
              ))}
              <Mui.Typography sx={designType.captionText}>
                Only saved rows are listed. Invalid and duplicate rows are shown in the import preview and are never saved.
              </Mui.Typography>
            </Mui.Stack>
          ) : (
            <SectionCard>
              <Mui.Typography sx={designType.tableBodyCell}>No imports yet. Use Add Bonus ▾ › Import Bonus to import a file.</Mui.Typography>
            </SectionCard>
          )}
        </GlobalSidePanel>
      ) : null}
    </ThemeProvider>
  );
};
